/* eslint-disable @angular-eslint/no-output-on-prefix */

import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';

import { format } from 'date-fns';
import { NgxMaskDirective } from 'ngx-mask';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import {
  FloatLabelType,
  MatFormFieldModule,
} from '@angular/material/form-field';
import {
  DatepickerDropdownPositionX,
  DatepickerDropdownPositionY,
  MatCalendarView,
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';

import { dateFormatValidator } from './validators';
import { CanAutoFocusDirective } from './directives';
import {
  DEFAULT_ERROR_STATE_MATCHER_PROVIDER,
  PROBATE_DATE_FORMATS_PROVIDER,
} from './providers';

import { ErrorMessagePipe } from '@shared/pipes';
import { ControlValueAccessorDirective } from '@shared/directives';

@Component({
  selector: 'app-ccs-date',
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CanAutoFocusDirective,
    ErrorMessagePipe,
    NgxMaskDirective,
  ],
  providers: [
    ...PROBATE_DATE_FORMATS_PROVIDER,
    DEFAULT_ERROR_STATE_MATCHER_PROVIDER,
  ],
  hostDirectives: [ControlValueAccessorDirective],
  templateUrl: './ccs-date.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CcsDateComponent<T> implements OnInit {
  @Input({ required: true }) label = '';
  @Input({ required: false }) placeholder = '';
  @Input({ required: false }) hint = '';
  @Input({ required: false }) startAt: Date | null = null;
  @Input({ required: false }) min: Date | null = null;
  @Input({ required: false }) max: Date | null = null;
  @Input({ required: false }) startView: 'month' | 'year' | 'multi-year' =
    'month';
  @Input({ required: false }) size: 'xs' | 'sm' | 'md' | 'lg' = 'sm';
  @Input({ required: false }) floatLabel: FloatLabelType = 'always';
  @Input({ required: false }) xPosition: DatepickerDropdownPositionX = 'start';
  @Input({ required: false }) yPosition: DatepickerDropdownPositionY = 'above';
  @Input({ required: false, transform: booleanAttribute }) touchUi = false;
  @Input({ required: false, transform: booleanAttribute }) autofocus = false;
  @Input({ required: false, transform: booleanAttribute }) restoreFocus = true;
  @Input({ required: false, transform: booleanAttribute }) showMaskTyped =
    false;
  @Input({ required: false, transform: booleanAttribute })
  dropSpecialCharacters = false;
  @Input({ required: false }) errorMessages: ValidationErrors | null = null;

  private readonly _va = inject(ControlValueAccessorDirective);

  public onOpened = output<void>();
  public onClosed = output<void>();
  public onBlur = output<FocusEvent>();
  public onFocus = output<FocusEvent>();
  public onYearSelected = output<Date>();
  public onMonthSelected = output<Date>();
  public onViewChanged = output<MatCalendarView>();
  public onDateChange = output<MatDatepickerInputEvent<Date>>();

  protected readonly picker = viewChild.required<MatDatepicker<Date>>('picker');

  get control(): FormControl<T | null> {
    return this._va.control;
  }

  ngOnInit(): void {
    this.control.addValidators(dateFormatValidator);
    this.control.updateValueAndValidity();
  }

  protected onDatePickerChange(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      const formatted: string = format(event.value, 'MM/dd/yyyy');
      this.control.setValue(formatted as unknown as T, { emitEvent: true });
      this.onDateChange.emit(event);
      this.picker().close();
    }
  }
}
